/* eslint-disable react/no-unstable-nested-components */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import FiltersButtonArray from "components/company/v3/FiltersButtonArray";
import useBrazilianIssuer from "components/company/v3/context/hooks/useBrazilianIssuer";
import { ScrollArea } from "components/ui/ScrollArea";
import TablePagination from "components/ui/Table/TablePagination";
import classNames from "components/ui/classNames";
import getYear from "date-fns/getYear";
import parseDateYmd from "lib/utils/parseDateYmd";
import orderBy from "lodash/orderBy";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IBrazilianCompanyIssuerOfficer } from "types/graphqlTypes";

interface BrazilianOfficersContentProps {
  isRenderPageDefault: boolean;
}

export default function BrazilianOfficersContent({ isRenderPageDefault }: BrazilianOfficersContentProps) {
  const [filter, setFilter] = useState<string>("EXECUTIVE_OFFICERS");

  const intl = useIntl();
  const issuer = useBrazilianIssuer();

  const columnHelper = createColumnHelper<IBrazilianCompanyIssuerOfficer>();

  const officers = useMemo(() => {
    const sortedOfficers = orderBy(
      issuer.officers.filter((node) => {
        if (filter === "EXECUTIVE_OFFICERS") {
          return node.board === "Pertence apenas à Diretoria";
        }
        if (filter === "BOARD_OF_DIRECTORS") {
          return node.board === "Pertence apenas ao Conselho de Administração";
        }
        return (
          node.board !== "Pertence apenas ao Conselho de Administração" && node.board !== "Pertence apenas à Diretoria"
        );
      }),
      ["board", "name"],
      ["desc", "asc"],
    );

    return [
      ...sortedOfficers.filter((officer) => {
        const isCEO = issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name;
        return isCEO;
      }),
      ...sortedOfficers.filter((officer) => {
        const isCEO = issuer.chiefExecutiveOfficer !== null && officer.name === issuer.chiefExecutiveOfficer.name;
        return !isCEO;
      }),
    ];
  }, [issuer.officers, issuer.chiefExecutiveOfficer, filter]);

  const columns = [
    columnHelper.accessor("name", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-left text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Nome" id="CH/6DN" />
        </div>
      ),
      cell: (info) => {
        const yearOfBirth = getYear(parseDateYmd(info.row.original.dateOfBirth));
        const age = getYear(new Date()) - yearOfBirth;
        return (
          <div
            className={classNames(
              "whitespace-nowrap px-4 py-1.5 text-sm font-normal text-slate-600 dark:text-slate-400",
            )}
          >
            <div className="flex items-center text-left">
              <div>
                <span className="font-bold text-slate-600 dark:text-slate-200">{info.getValue()}</span>

                <span className="flex text-sm text-slate-500">
                  <FormattedMessage
                    defaultMessage="{position} • {age} anos"
                    description="Informações de cargo e idade do executivo"
                    id="COQBH2"
                    values={{
                      position: info.row.original.position,
                      age,
                    }}
                  />
                </span>
              </div>

              {issuer.chiefExecutiveOfficer !== null &&
                info.row.original.name === issuer.chiefExecutiveOfficer.name && (
                  <div className="ml-3">
                    <span className="rounded-md bg-blue-600/10 px-2 py-1 text-xs font-bold tracking-wider text-blue-700 dark:bg-blue-400/20 dark:text-blue-400">
                      CEO
                    </span>
                  </div>
                )}
            </div>
          </div>
        );
      },
    }),
    columnHelper.accessor("board", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Quadro" id="7PckAn" />
        </div>
      ),
      cell: (info) => (
        <span
          className={classNames(
            "whitespace-nowrap rounded-md px-2 py-1 text-xs font-bold tracking-wide text-slate-600 dark:text-slate-400",
            getBoardTagClassName(info.getValue()),
          )}
        >
          {getCleanBoard(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("termOfOffice", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Mandato" id="kBgOJ9" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "flex flex-col justify-center whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-400",
          )}
        >
          <div className="mx-auto max-w-[280px] overflow-hidden text-ellipsis whitespace-nowrap font-bold text-slate-600 dark:text-slate-200">
            {info.getValue()}
          </div>

          <span className="text-xs font-medium tracking-wider text-slate-500">
            <FormattedMessage defaultMessage="Eleito em" id="g2vyqS" description="Dia em que o executivo foi eletio" />{" "}
            <time dateTime={info.row.original.electedDate}>
              {intl.formatDate(parseDateYmd(info.row.original.electedDate), {
                year: "numeric",
                month: "numeric",
                day: "numeric",
              })}
            </time>
          </span>
        </div>
      ),
    }),
  ];

  const table = useReactTable({
    data: officers,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: isRenderPageDefault ? 6 : 10,
      },
    },
  });

  return (
    <div className="flex h-auto w-full flex-col space-y-4">
      <FiltersButtonArray
        filter={filter}
        setFilter={setFilter}
        filters={[
          {
            key: "EXECUTIVE_OFFICERS",
            label: intl.formatMessage(
              {
                defaultMessage: "Diretoria ({count})",
                id: "k/JxpM",
              },
              {
                count: issuer.officers.filter((node) => node.board === "Pertence apenas à Diretoria").length,
              },
            ),
          },
          {
            key: "BOARD_OF_DIRECTORS",
            label: intl.formatMessage(
              {
                defaultMessage: "Conselho de Administração ({count})",
                id: "UMlBg1",
              },
              {
                count: issuer.officers.filter((node) => node.board === "Pertence apenas ao Conselho de Administração")
                  .length,
              },
            ),
          },
          {
            key: "SUPERVISORY_BOARD",
            label: intl.formatMessage(
              {
                defaultMessage: "Conselho Fiscal ({count})",
                id: "lJr3+m",
              },
              {
                count: issuer.officers.filter(
                  (node) =>
                    node.board !== "Pertence apenas ao Conselho de Administração" &&
                    node.board !== "Pertence apenas à Diretoria",
                ).length,
              },
            ),
          },
        ]}
      />

      <div className="flex h-full w-full flex-col items-end space-y-2">
        <ScrollArea type="auto" orientation="horizontal" className="relative h-full w-full">
          <div className="lg:w-full">
            <div
              className={classNames("relative overflow-auto rounded-xl border border-slate-200 dark:border-slate-950")}
            >
              <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                <thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr
                      key={headerGroup.id}
                      className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                    >
                      {headerGroup.headers
                        .filter((header) => !header.column.columnDef.meta?.isHidden)
                        .map((header) => {
                          return (
                            <th
                              key={header.id}
                              className={classNames(
                                "m-0 bg-slate-50 p-0 text-center dark:bg-slate-800",
                                header.column.columnDef.meta?.header?.className,
                              )}
                            >
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </th>
                          );
                        })}
                    </tr>
                  ))}
                </thead>
                <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                  {table.getRowModel().rows.map((row) => {
                    return (
                      <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                        {row
                          .getVisibleCells()
                          .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                          .map((cell) => {
                            return (
                              <td
                                key={cell.id}
                                className={classNames(
                                  "m-0 p-0 text-center",
                                  cell.column.columnDef.meta?.cell?.className,
                                )}
                              >
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                              </td>
                            );
                          })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </ScrollArea>

        <TablePagination<IBrazilianCompanyIssuerOfficer> table={table} />
      </div>
    </div>
  );
}

function getBoardTagClassName(board: string) {
  if (board === "Pertence apenas à Diretoria") {
    return "bg-green-600/10 text-green-800 dark:bg-green-400/20 dark:text-green-400";
  }
  if (board === "Pertence apenas ao Conselho de Administração") {
    return "bg-blue-600/10 text-blue-800 dark:bg-blue-400/20 dark:text-blue-400";
  }
  return "bg-slate-600/10 text-slate-800 dark:bg-slate-400/20 dark:text-slate-300";
}

function getCleanBoard(board: string) {
  if (board === "Pertence apenas à Diretoria") {
    return <FormattedMessage defaultMessage="Diretoria" id="tjB3ju" />;
  }
  if (board === "Pertence apenas ao Conselho de Administração") {
    return <FormattedMessage defaultMessage="Conselho de Administração" id="R87gu4" />;
  }
  return <FormattedMessage defaultMessage="Conselho Fiscal" id="zf/kwn" />;
}
